// A projeção de uma oferta de hotel para a view que o card, a lista e o mapa
// desenham — pura, sem React. Uma fonte para as superfícies do planner (card da
// conversa, painel, página da bancada) e para o cabeçalho do escolhido no
// `hotel_rooms`.
//
// A entrada é ESTRUTURAL (`HotelOfferSource`): o kit não importa o wire. As
// palavras vêm do host (`HotelOfferViewCopy`), e os FATOS que dependem da régua
// do wire também (`HotelOfferFacts`): a distância e o deslocamento são os mesmos
// que o agente lê, e o motivo da exceção de política é texto montado com os
// limites da política. O kit só os pinta.
import type {
  HotelAmenityCode,
  HotelAmenityIcon,
  HotelAmenitySource,
  HotelAmenityView,
  HotelAxis,
  HotelOfferFacts,
  HotelOfferSource,
  HotelOfferView,
  HotelOfferViewCopy,
} from '../models.js';
import { hotelPhotoAlt } from '../photo_alt.js';
import { hotelPhotoUrl } from '../photo_url.js';
import { ratingOrUndefined } from '../rating_band.js';
import type { HotelFilterableOffer } from './hotel_results_filters.js';
import { agentPickBadge, hotelAxisByOffer, offerBadges } from './hotel_offer_badges.js';

/** Catálogo → ícone. Mapa 1:1 fechado pelo enum: comodidade nova entra pelo
 *  `code` (e o TypeScript cobra a linha aqui), nunca por adivinhação de texto. */
const AMENITY_ICONS: Readonly<Record<HotelAmenityCode, HotelAmenityIcon>> = {
  wifi: 'wifi',
  breakfast: 'breakfast',
  parking: 'parking',
  pool: 'pool',
  gym: 'fitness',
  restaurant: 'restaurant',
  laundry: 'other',
};

function photoViews(
  offer: HotelOfferSource,
  proxyImageUrl: (url: string) => string,
): HotelOfferView['photos'] {
  const photos = offer.photos?.length
    ? offer.photos
    : offer.thumbnailUrl
      ? [{ url: offer.thumbnailUrl }]
      : [];
  return photos.map((photo, index) => ({
    id: `${offer.offerId}-photo-${String(index + 1)}`,
    url: proxyImageUrl(hotelPhotoUrl(photo.url)),
    alt: hotelPhotoAlt(photo.description, offer.title),
  }));
}

/** `code` presente → rótulo e ícone nossos, nos três locales. Sem `code` (id fora
 *  do catálogo do fornecedor), sobra o texto dele — em português, mas presente.
 *  O `id` é a identidade da comodidade, e é por ele que o filtro de resultados
 *  agrupa a mesma comodidade entre ofertas diferentes. */
export function hotelAmenityViewsOf(
  amenities: readonly HotelAmenitySource[],
  copy: Pick<HotelOfferViewCopy, 'amenity'>,
): HotelAmenityView[] {
  return amenities.map((amenity) => ({
    id: amenity.code ?? amenity.label,
    label: amenity.code ? copy.amenity(amenity.code) : amenity.label,
    icon: amenity.code ? AMENITY_ICONS[amenity.code] : 'other',
  }));
}

export function hotelTravelLabelsOf(
  facts: Pick<HotelOfferFacts, 'distanceKm' | 'travel'>,
  copy: Pick<HotelOfferViewCopy, 'decimal' | 'distance' | 'travelTime'>,
): Pick<HotelOfferView, 'distanceLabel' | 'travelTimeLabel'> {
  if (facts.distanceKm === undefined) return {};
  return {
    distanceLabel: copy.distance(copy.decimal(facts.distanceKm)),
    ...(facts.travel ? { travelTimeLabel: copy.travelTime(facts.travel) } : {}),
  };
}

function offerPrice(
  offer: HotelOfferSource,
  nights: number | undefined,
  copy: HotelOfferViewCopy,
): Pick<HotelOfferView, 'priceLabel' | 'priceDetail'> {
  if (!offer.price) return { priceLabel: copy.priceUnavailable };
  if (!nights) return { priceLabel: copy.money(offer.price) };
  return {
    priceLabel: copy.money({ ...offer.price, amount: offer.price.amount / nights }),
    priceDetail: copy.perNight,
  };
}

/** A linha de reembolso do card (#3404). `refundable` é do quarto EXIBIDO;
 *  `refundableRateFrom` é o preço da tarifa reembolsável mais barata, que pode
 *  ser outro quarto. Quando o exibido já é reembolsável, a palavra basta; senão
 *  o card diz a partir de quanto, na MESMA régua do preço principal (diária
 *  quando as noites são conhecidas). Sem nenhum dos dois, nada — chip de
 *  "reembolsável" ao lado de um preço que não é dela era a divergência da issue. */
function offerRefundableLabel(
  offer: HotelOfferSource,
  nights: number | undefined,
  copy: HotelOfferViewCopy,
): Pick<HotelOfferView, 'refundableLabel'> {
  if (offer.refundable === true) return { refundableLabel: copy.refundable };
  const from = offer.refundableRateFrom;
  if (!from) return {};
  const price = copy.money(nights ? { ...from, amount: from.amount / nights } : from);
  return { refundableLabel: copy.refundableFrom(price) };
}

/** O que o card precisa saber ALÉM da oferta: a estadia (para a diária), os
 *  fatos do host (distância, deslocamento, motivo da exceção) e quem é a mais
 *  barata do conjunto (para o selo). O card de hotel é um átomo só, então quem
 *  hospeda passa o contexto — o `hotel_rooms` mostra o escolhido sem ter as
 *  outras ofertas em mãos. */
export type HotelOfferViewContext = {
  copy: HotelOfferViewCopy;
  proxyImageUrl: (url: string) => string;
  detailsAvailable: boolean;
  nights?: number;
  facts?: HotelOfferFacts;
  /** O eixo deste card, decidido para a lista inteira por `hotelAxisByOffer`.
   *  Ausente fora da tela de resultados — o cabeçalho do hotel escolhido monta
   *  a mesma view e ali não há trio para comparar. */
  axis?: HotelAxis;
  /** #3399 (b): o bloco é um RECORTE de uma busca maior (`offersTotal` acima do
   *  que ele carrega) — o rótulo do eixo de preço/distância diz "no cartão".
   *  Ausente = o bloco é o conjunto, ou ninguém sabe o total (bloco antigo). */
  axisOnCard?: boolean;
  /** M1 · 1.2 — este card está nos `directives.highlightOfferIds` do agente. O
   *  destaque VENCE os outros selos (favorito/estadia/eixo) — o mesmo "um selo
   *  por card" que já vale hoje, só que a decisão de quem manda passou a
   *  incluir o agente. */
  highlighted?: boolean;
};

/** O motivo concreto da exceção, para o tooltip do escudo do card — ausente
 *  quando a oferta não traz as regras quebradas (o rótulo genérico basta). */
function outOfPolicyReason(
  facts: HotelOfferFacts | undefined,
): Pick<HotelOfferView, 'outOfPolicyReason'> {
  const reasons = facts?.outOfPolicyReasons ?? [];
  return reasons.length > 0 ? { outOfPolicyReason: reasons.join('; ') } : {};
}

export function hotelOfferViewOf(
  offer: HotelOfferSource,
  context: HotelOfferViewContext,
): HotelOfferView {
  const { copy } = context;
  const rating = ratingOrUndefined(offer.rating);
  const shownRating = rating === undefined ? undefined : copy.decimal(rating);
  return {
    id: offer.offerId,
    name: offer.title,
    ...(offer.summary ? { summary: offer.summary } : {}),
    ...(offer.address || offer.city ? { locationLabel: offer.address ?? offer.city } : {}),
    photos: photoViews(offer, context.proxyImageUrl),
    ...(offer.latitude !== undefined && offer.longitude !== undefined
      ? { coordinates: { latitude: offer.latitude, longitude: offer.longitude } }
      : {}),
    // `label` segue com a palavra ("nota 4,2") porque é o nome ACESSÍVEL e o
    // que o card de quartos escreve por extenso; `valueLabel` é só o número
    // formatado, que é o que o card de oferta mostra ao lado da estrela
    // (protótipo: estrela cheia + número + · + distância, sem a palavra).
    ...(shownRating
      ? { rating: { label: copy.rating(shownRating), valueLabel: shownRating } }
      : {}),
    ...hotelTravelLabelsOf(context.facts ?? {}, copy),
    ...offerPrice(offer, context.nights, copy),
    badges: context.highlighted
      ? [agentPickBadge(copy.badges)]
      : offerBadges(offer, context.axis, copy.badges, context.axisOnCard),
    // O estado do coração (`aria-pressed`) — distinto do selo: o selo é UM por
    // card, o coração existe em todo card quando o host liga o toggle.
    ...(offer.favorite === undefined ? {} : { favorite: offer.favorite }),
    ...(offer.policyStatus === 'out_of_policy'
      ? { outOfPolicyLabel: copy.outOfPolicy, ...outOfPolicyReason(context.facts) }
      : {}),
    ...offerRefundableLabel(offer, context.nights, copy),
    ...(offer.agreedRate ? { agreedRateLabel: copy.badges.agreedRate } : {}),
    amenities: hotelAmenityViewsOf(offer.amenities ?? [], copy),
    detailsAvailable: context.detailsAvailable,
    bookable: Boolean(offer.price),
  };
}

/**
 * As mesmas vistas de oferta do card, para uma lista que NÃO veio do bloco — a
 * página do painel. Sem a reordenação do baralho: ali a ordem é a do fornecedor
 * sobre o conjunto inteiro, e puxar o selecionado para a frente embaralharia a
 * paginação.
 */
export function hotelOfferViewsOf<T extends HotelOfferSource>(
  offers: readonly T[],
  options: {
    copy: HotelOfferViewCopy;
    factsOf: (offer: T) => HotelOfferFacts;
    proxyImageUrl?: (url: string) => string;
    detailsAvailable?: boolean;
    nights?: number;
  },
): HotelOfferView[] {
  const facts = new Map(offers.map((offer) => [offer.offerId, options.factsOf(offer)]));
  const axisByOffer = hotelAxisByOffer(offers, (offer) => facts.get(offer.offerId)?.distanceKm);
  return offers.map((offer) => {
    const axis = axisByOffer.get(offer.offerId);
    return hotelOfferViewOf(offer, {
      copy: options.copy,
      proxyImageUrl: options.proxyImageUrl ?? ((url: string) => url),
      detailsAvailable: options.detailsAvailable ?? true,
      ...(options.nights === undefined ? {} : { nights: options.nights }),
      facts: facts.get(offer.offerId) ?? {},
      ...(axis ? { axis } : {}),
    });
  });
}

/** Os fatos de TARIFA do hotel para o filtro, cada um só quando o wire o
 *  traz. Fora de `hotelFilterableOffersOf` para ele não passar do teto de
 *  complexidade. */
function rateFactsOf(source: HotelOfferSource): Partial<HotelFilterableOffer> {
  return {
    // Agregados de tarifa do hotel (itens 6/7): tri-estado preservado —
    // `undefined` (o wire não trouxe) fica ausente e o toggle nem aparece.
    ...(source.hasBreakfastRate === undefined ? {} : { hasBreakfast: source.hasBreakfastRate }),
    ...(source.hasRefundableRate === undefined ? {} : { hasRefundable: source.hasRefundableRate }),
    // #6928: só o `true` viaja (hotel sem acordo não diz nada).
    ...(source.agreedRate === true ? { agreedRate: true } : {}),
    // #5820: a tarifa com café E reembolso e a política de cada tarifa
    // com atributo, mesmo tri-estado.
    ...(source.hasBreakfastRefundableRate === undefined
      ? {}
      : { hasBreakfastRefundable: source.hasBreakfastRefundableRate }),
    ...(source.breakfastRateWithinPolicy === undefined
      ? {}
      : { breakfastRateWithinPolicy: source.breakfastRateWithinPolicy }),
    ...(source.refundableRateWithinPolicy === undefined
      ? {}
      : { refundableRateWithinPolicy: source.refundableRateWithinPolicy }),
    ...(source.breakfastRefundableRateWithinPolicy === undefined
      ? {}
      : { breakfastRefundableRateWithinPolicy: source.breakfastRefundableRateWithinPolicy }),
  };
}

/** Os fatos de corte e de ordem de cada linha, para o filtro e a ordenação do
 *  kit (`matchesHotelResultsFilters`, `sortHotelOffers`). */
export function hotelFilterableOffersOf<T extends HotelOfferSource>(
  offers: readonly HotelOfferView[],
  sourceOffers: readonly T[],
  context: {
    nights: number | undefined;
    currency: string;
    /** A MESMA régua do rótulo do card: o corte "até 3 km" e o "2,4 km"
     *  impressos têm de sair do mesmo número, senão o filtro descarta um hotel
     *  que a linha ao lado dizia estar dentro. */
    distanceOf: (offer: T) => number | undefined;
  },
): HotelFilterableOffer[] {
  return offers.flatMap((view) => {
    const source = sourceOffers.find((candidate) => candidate.offerId === view.id);
    if (!source) return [];
    // O eixo de preço do filtro é UM número numa moeda só (a régua e o rótulo
    // "Diária até US$ X" vêm da moeda da lista). Uma oferta em outra moeda não é
    // comparável: entra sem `nightlyPrice` e some quando um teto está ativo, em
    // vez de ser medida como se 100 BRL fossem 100 USD.
    const nightlyPrice =
      source.price && source.price.currency === context.currency
        ? source.price.amount / Math.max(1, context.nights ?? 1)
        : undefined;
    const rating = ratingOrUndefined(source.rating);
    const distance = context.distanceOf(source);
    return [
      {
        offer: view,
        withinPolicy: source.policyStatus === 'within_policy',
        ...(nightlyPrice === undefined ? {} : { nightlyPrice }),
        ...(rating === undefined ? {} : { rating }),
        ...(distance === undefined ? {} : { distance }),
        ...(source.reviewCount === undefined ? {} : { reviewCount: source.reviewCount }),
        ...rateFactsOf(source),
      },
    ];
  });
}
